import { Box, Button, Stack, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { Resources, useResource } from "Translations/Resources";
import {
  FACILITY_PHOTO_ACCEPTED_TYPES,
  FACILITY_PHOTO_MAX_SIZE_BYTES,
  FACILITY_PHOTO_MAX_SIZE_MB,
} from "Components/Shared/Merchant/Facilities/constants";

const RESOURCES = Resources.Components.Merchant.Facilities.Form;
const ACCEPT_ATTRIBUTE = FACILITY_PHOTO_ACCEPTED_TYPES.join(",");

export type PhotoState = {
  hasExisting: boolean;
  existingUrl?: string | null;
  pendingFile: File | null;
  /**
   * Resource key of why the last pick or upload was rejected — shown under the buttons until
   * the next pick. A key rather than text so it follows a language switch.
   */
  errorResource?: string | null;
};

type Props = {
  label: string;
  photo: PhotoState;
  onChange: (file: File | null) => void;
  onError: (resource: string | null) => void;
  onDelete?: () => void;
};

export const PhotoInput = ({
  label,
  photo,
  onChange,
  onError,
  onDelete,
}: Props) => {
  const { t } = useResource();
  const { hasExisting, existingUrl, pendingFile, errorResource } = photo;
  const hasPhoto = hasExisting || pendingFile != null;

  // Same limits the server enforces — rejecting here gives the reason immediately
  // instead of after a round trip, and never lets the file into the pending state.
  const handleFilePick = (file: File | null) => {
    if (!file) {
      return;
    }
    if (!FACILITY_PHOTO_ACCEPTED_TYPES.includes(file.type)) {
      onError(RESOURCES.PhotoInvalidType);
      return;
    }
    if (file.size > FACILITY_PHOTO_MAX_SIZE_BYTES) {
      onError(RESOURCES.PhotoTooLarge);
      return;
    }
    onError(null);
    onChange(file);
  };

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">
        {label}
        {hasPhoto && (
          <CheckCircleIcon
            color="success"
            sx={{ ml: 1, fontSize: 18, verticalAlign: "text-bottom" }}
          />
        )}
      </Typography>

      {existingUrl && !pendingFile && (
        <Box
          component="img"
          src={existingUrl}
          alt={label}
          sx={{
            maxWidth: 200,
            maxHeight: 150,
            borderRadius: 1,
            objectFit: "cover",
          }}
        />
      )}

      {pendingFile && (
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.PhotoSelected, { name: pendingFile.name })}
        </Typography>
      )}

      <Stack direction="row" gap={1}>
        <Button
          variant="outlined"
          size="small"
          component="label"
          startIcon={<CloudUploadIcon />}
        >
          {hasPhoto
            ? t(RESOURCES.PhotoChangeButton)
            : t(RESOURCES.PhotoUploadButton)}
          <input
            type="file"
            hidden
            accept={ACCEPT_ATTRIBUTE}
            onChange={(e) => {
              handleFilePick(e.target.files?.[0] ?? null);
              e.target.value = "";
            }}
          />
        </Button>

        {hasExisting && onDelete && !pendingFile && (
          <Button
            variant="outlined"
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={onDelete}
          >
            {t(RESOURCES.PhotoDeleteButton)}
          </Button>
        )}
      </Stack>

      {errorResource && (
        <Typography variant="caption" color="error">
          {t(errorResource, { maxSizeMb: FACILITY_PHOTO_MAX_SIZE_MB })}
        </Typography>
      )}
    </Stack>
  );
};
